import { ContentStatus, HeadphonesRecommendation } from './index';
// Kanonische Produkttyp-Taxonomie (Single Source of Truth)
import type { ProductType } from './productType';

// Hier standen vier Weiterreichungen der Produkttyp-Taxonomie: ein Re-Export von
// `ProductType`/`ProductTypeLabels` und die Aliasse `ProductCategory`/`ProductCategoryLabels`.
// Keine davon hatte einen Verbraucher — alle acht Aufrufstellen importieren direkt aus
// `./productType`. Übrig bleibt der Typ-Alias, den `AudioContent`/`AudioFormData` unten
// tatsächlich benutzen; er zeigt jetzt ohne Umweg auf die kanonische Quelle.
export type ProductCategory = ProductType;

// Audio Stream Types
export enum AudioStreamType {
  SILENT = 'silent',
  AUDIO = 'audio',
  FREQUENZ = 'frequenz',
  NATURE = 'nature'
}

// Audio File Format
export type AudioFormat = 'mp3' | 'flac';

// Audio File Interface
export interface AudioFile {
  url: string;
  format: AudioFormat;
  size?: number; // in bytes
  bitrate?: number; // in kbps
  duration?: number; // in seconds
}

// Audio Stream Interface (max 4 streams)
export interface AudioStream {
  type: AudioStreamType;
  mp3: AudioFile; // Required MP3 file
  flac?: AudioFile; // Optional FLAC file (only for SILENT stream)
  volume: number; // 0-100
  enabled: boolean;
}

// Audio Streams (up to 4 parallel streams)
export interface AudioStreams {
  silent?: AudioStream; // MP3 + optional FLAC
  audio?: AudioStream; // MP3 only
  frequenz?: AudioStream; // MP3 only
  nature?: AudioStream; // MP3 only
}

// Audio Metadata — die `metadata`-Spalte einer AUDIO-Zeile, so wie /api/audio sie WIRKLICH liefert.
//
// Der Feldsatz unten ist am 2026-08-10 an der dev-Datenbank abgelesen, nicht geraten:
//   SELECT type, k FROM content c, LATERAL jsonb_object_keys(c.metadata) k GROUP BY 1,2
// ergibt für AUDIO exakt diese 24 Schlüssel. Alles außer `title` ist optional, weil das
// Backend nur schreibt, was das Formular geschickt hat.
//
// ⚠️ `duration` war als `number` deklariert und ist in Wahrheit ein String („10 Minuten").
// Geschrieben wird er in audioService.transformFormDataToBackendFormat aus einem Textfeld,
// gelesen wird er wieder in ein Textfeld. Die falsche Deklaration ist nie aufgefallen, weil
// jeder Lesezugriff über ein `as any` lief.
export interface AudioMetadata {
  title: string;
  artist?: string;
  album?: string;
  albumId?: string | null;
  artistId?: string | null;
  genre?: string;
  duration?: string; // Anzeige-Text, z. B. „10 Minuten" — NICHT Sekunden (siehe Hinweis oben)
  isrc?: string; // International Standard Recording Code
  year?: number | null;
  language?: string;
  bpm?: number | null; // Beats per minute
  key?: string; // Musical key
  coverImage?: string;
  description?: string;
  shortDescription?: string;
  keywords?: string;
  headphones?: string;
  /** Kopfhörer-Empfehlung je Variante — nur die Slots, für die etwas gesetzt wurde. */
  headphonesByVariant?: Partial<Record<VariantSlot, string>>;
  topicCategories?: string[];
  productCategory?: string;
  affirmations?: string[];
  frequencies?: string[];
  playlistIds?: string[];
  productIds?: string[];
  lossless?: boolean;
}

// Die `contentData`-Spalte einer AUDIO-Zeile.
//
// In der dev-Datenbank kommen dort nur `audioBundles` und `variantTracks` vor. `streams`
// steht NICHT in der Spalte, sondern wird von /api/audio zur Laufzeit dazugerechnet — die
// Antwort trägt es deshalb sowohl unter `contentData.streams` als auch auf oberster Ebene
// (beides am 2026-08-10 an der laufenden dev-API geprüft).
export interface AudioContentData {
  streams?: AudioStreams;
  audioBundles?: AudioBundle[];
  /** Zusätzliche Spuren je Variante (position ≥ 1). Leere Slots sind normal. */
  variantTracks?: Partial<Record<VariantSlot, VariantExtraTrack[]>>;
}

// Audio Bundle (for product bundles)
export interface AudioBundle {
  id: string;
  name: string;
  description?: string;
  audioIds: string[]; // Array of audio content IDs
  price?: number;
  currency?: string;
  discount?: number; // Percentage discount
}

// Audio Content Status
// ⚠️ Hier stand ein eigenes `AudioStatus`-Enum mit drei Stufen. Es ist gelöscht: Audio-Inhalte
// SIND Content-Zeilen, das Prisma-Schema kennt genau ein `ContentStatus` mit vier Stufen, und die
// Audio-Route schickt ihren Status durch dieselbe Backend-Funktion `normalizeStatus` wie die
// Inhalte-Route. Die dritte Kopie ohne `REJECTED` hieß: ein abgelehnter Audio-Inhalt konnte
// seinen eigenen Zustand nicht darstellen. Begründung in voller Länge bei `ContentStatus`.

// Audio Content Interface (Core Entity)
export interface AudioContent {
  id: string;
  title: string;
  description: string;
  shortDescription?: string; // Kurzbeschreibung
  duration?: string; // Dauer (formatted string)
  headphones?: HeadphonesRecommendation; // Kopfhörer-Empfehlung
  keywords?: string; // Keywords
  /** Unterthemen-Slugs. Seit die Taxonomie in der Datenbank liegt (PR #623) sind das
   *  Laufzeit-Werte — ein Compile-Zeit-Enum kann sie nicht mehr abbilden. Gültigkeit
   *  sichert die Auswahl-Komponente, nicht der Typ. */
  topicCategories?: string[];
  productCategory?: ProductCategory; // Produktkategorie
  
  // Audio Streams (up to 4 parallel streams)
  streams: AudioStreams;
  
  // Audio Metadata
  metadata: AudioMetadata;
  
  // Relations
  artistId?: string;
  albumId?: string;
  playlistIds?: string[]; // Array of playlist IDs
  productIds?: string[]; // Array of product IDs (optional - audio can exist without products)
  
  // Audio Bundles
  audioBundles?: AudioBundle[]; // JSON array/object for bundles
  
  // Zugriff (einziges Feld: true=Premium, false=Free)
  isPremium: boolean;

  // Status
  status: ContentStatus;
  
  // Timestamps
  createdAt: string;
  updatedAt: string;
  
  // Author
  author?: {
    id: string;
    username: string;
  };
  
  // Tags
  tags?: string[];

  // ——— Was /api/audio zusätzlich auf oberster Ebene liefert ———
  // Am 2026-08-10 an der laufenden dev-API abgelesen. Bis dahin las der Code diese Felder
  // ausschließlich über `as any` — mit der Folge, dass niemand merkte, welche davon es gar
  // nicht gibt (`coverImage`, `coverImageUrl`, `shortDescription`, `longDescription`,
  // `headphones`, `topicCategories`, `variantTracks` stehen NUR in `metadata` bzw.
  // `contentData`, nie oben). Deshalb steht hier nur, was wirklich kommt.
  type?: string;
  slug?: string;
  locale?: string;
  language?: string;
  /** Rohe contentData-Spalte. `streams` daraus spiegelt normalizeAudioContent nach oben. */
  contentData?: AudioContentData;
  /** Anzeige-Spiegel der Interpret-/Album-Zuordnung (Prisma-Spalten content.artist/album). */
  artist?: string | null;
  album?: string | null;
  /** Aufgelöster Interpret. Kann null sein, wenn kein Interpret zugeordnet ist. */
  artistRef?: { id: string; name?: string; slug?: string } | null;
  durationMs?: number | null;
  trackNo?: number | null;
  genre?: string | null;
  bpm?: number | null;
  mood?: string | null;
  category?: string | null;
  playCount?: number;
  averageRating?: number | null;
  isFeatured?: boolean;
  version?: number;
  authorId?: string;
  publishedAt?: string | null;
}

// Slot-Kennung für eine Varianten-Spur. Die vier Werte standen hier ein zweites Mal
// von Hand — dieselben vier wie in `AudioStreamType`, nur als Zeichenketten-Union.
// Die Vorlage `` `${…}` `` leitet sie jetzt aus dem Enum ab: dieselbe Union wie zuvor,
// aber ein hinzugefügter Stream-Typ kommt hier von selbst an, statt vergessen zu werden.
export type VariantSlot = `${AudioStreamType}`;

// Zusätzliche Varianten-Spur (position ≥1) — additiv zum Haupt-Stream (position 0) derselben Variante.
// Beispiel: mehrere Nature-Spuren (Regen, Wald, …) unter der Nature-Variante.
export interface VariantExtraTrack {
  slot: VariantSlot;
  title: string;
  mp3Url: string;
  enabled: boolean;
}

// Audio Form Data (for form input)
export interface AudioFormData {
  title: string;
  description: string;
  shortDescription?: string;
  duration?: string;
  headphones?: HeadphonesRecommendation;
  /** Unterthemen-Slugs. Seit die Taxonomie in der Datenbank liegt (PR #623) sind das
   *  Laufzeit-Werte — ein Compile-Zeit-Enum kann sie nicht mehr abbilden. Gültigkeit
   *  sichert die Auswahl-Komponente, nicht der Typ. */
  topicCategories?: string[];
  productCategory?: ProductCategory; // Produktkategorie
  
  // Stream files (separate fields for each stream)
  silentMp3File?: File;
  silentMp3Url?: string;
  silentFlacFile?: File;
  silentFlacUrl?: string;
  silentVolume?: number;
  silentEnabled?: boolean;
  
  audioMp3File?: File;
  audioMp3Url?: string;
  audioVolume?: number;
  audioEnabled?: boolean;
  
  frequenzMp3File?: File;
  frequenzMp3Url?: string;
  frequenzVolume?: number;
  frequenzEnabled?: boolean;
  
  natureMp3File?: File;
  natureMp3Url?: string;
  natureVolume?: number;
  natureEnabled?: boolean;

  // Kopfhörer-Empfehlung PRO VARIANTE (recommended | not_recommended | not_required | '' = keine).
  // Save bündelt sie zu metadata.headphonesByVariant; Load liest von dort.
  audioHeadphones?: string;
  silentHeadphones?: string;
  frequenzHeadphones?: string;
  natureHeadphones?: string;

  // Affirmationen — im Formular eine pro Zeile (Textarea-Roh-String); Save splittet zu
  // metadata.affirmations (string[]), Load joint von dort zurück.
  affirmations?: string;

  // Frequenzen — eine pro Zeile (Textarea-Roh-String), in der Frequenz-Stream-Konfig; Save splittet
  // zu metadata.frequencies (string[]), Load joint von dort zurück.
  frequencies?: string;

  // Metadata
  artist?: string;
  album?: string;
  language?: string;
  coverImage?: File;
  coverImageUrl?: string;
  
  // Zusätzliche Varianten-Spuren (N-pro-kind) — position ≥1, additiv zum Haupt-Stream (position 0).
  // Backend gruppiert nach slot → contentData.variantTracks[slot], syncVariantExtras schreibt Track-Entities.
  variantTracks?: VariantExtraTrack[];

  // Relations
  artistId?: string;
  albumId?: string;
  playlistIds?: string[];
  productIds?: string[];

  // Zugriff (einziges Feld: true=Premium, false=Free)
  isPremium: boolean;

  // Tags
  tags?: string[];

  // Status/Bundle (Backend-Kompatibilität)
  status?: ContentStatus;
  audioBundles?: AudioBundle[];
}

// Audio Filters
export interface AudioFilters {
  page: number;
  limit: number;
  search?: string;
  status?: ContentStatus;
  isPremium?: boolean;
  artistId?: string;
  albumId?: string;
  genre?: string;
  // ⚠️ `string`, nicht das alte `SubTopicCategory`-Enum: die Themen-Taxonomie ist seit PR #623
  // zur Laufzeit pflegbar. Ein fester Union-Typ hieße, dass ein im CMS neu angelegtes Thema
  // ohne Code-Änderung nicht als Filterwert taugt — genau die Kopplung, die die Schiene löst.
  // (Backend filtert darauf: audioMapping.ts / videoMapping.ts, `array_contains`.)
  topicCategory?: string;
  // Produkttyp-Filter (ProductType-Wert, z.B. 'audio-subliminal'). Wird generisch
  // als URL-Param mitgeschickt; das Backend matcht `category` gegen `productCategory`.
  category?: string;
}

// Paginated Response for Audio
// Paginierungs-Hülle: kanonisch in ./index. Hier nur re-exportiert, damit bestehende
// Importe aus '../types/audio' weiter funktionieren.
export type { PaginatedResponse, ApiPagination } from './index';

// ── Anzeigenamen der Varianten ───────────────────────────────────────────────────────
//
// EINE Quelle für jede Stelle, die eine Variante benennt. Zuvor stand diese Zuordnung
// fünfmal von Hand im Code: dreimal als `KIND_LABELS` (Spuren-Wähler, Album-Formular,
// Playlist-Formular) und zweimal im Audio-Formular (`VARIANT_SLOTS`, `VARIANTS`) — während
// die Tabelle hier, die es dafür längst gab, keinen einzigen Verbraucher hatte.
//
// Sie war dadurch bereits auseinandergelaufen: hier stand „Nature", in allen fünf
// Handkopien „Natur". Übernommen ist „Natur" — das ist der Text, den die Oberfläche
// heute zeigt, und den die Zusicherungen in `AudioForm.test.tsx` festhalten.
//
// Der Schlüsseltyp ist bewusst `` `${AudioStreamType}` `` und nicht `AudioStreamType`:
// die Vorlage macht aus dem String-Enum seine reinen Werte, sodass sich die Tabelle auch
// mit einem `VariantSlot` nachschlagen lässt. Die Vollständigkeits-Prüfung bleibt: ein
// neuer Enum-Wert ohne Eintrag hier ist ein Übersetzungsfehler.
export const AudioStreamTypeLabels: Record<`${AudioStreamType}`, string> = {
  silent: 'Silent',
  audio: 'Audio',
  frequenz: 'Frequenz',
  nature: 'Natur',
};

// Anzeigenamen der Spur-Arten, wie das Backend sie in `track.kind` liefert: die vier
// Varianten GROSS geschrieben (so steht das Prisma-Enum in der Datenbank) plus `CHAPTER`
// für MindBook-Kapitel — das ist keine Variante und steht deshalb nur hier, nicht oben.
export const TrackKindLabels: Record<string, string> = {
  ...Object.fromEntries(
    Object.values(AudioStreamType).map((typ) => [typ.toUpperCase(), AudioStreamTypeLabels[typ]]),
  ),
  CHAPTER: 'Kapitel',
};
